import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import {
  OrganisationListItem,
  organisationListQueryGraphql,
} from "@/app/Organisations/GraphQL/organisationListQuery.graphql";
import type { TableId } from "@/app/Common/Utils/table-id";

import { useOrganisationActions } from "@/app/Organisations/Hooks/useOrganisationActions";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { BadgeCell } from "@/lib/Components/DataTable/Cells/BadgeCell";
import { QueryOrganisationsOrderByColumn, SortOrder } from "@/gql/graphql";
import { useState } from "react";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { organisationVerificationCountsQuery } from "@/app/Organisations/GraphQL/organisationVerificationCountsQuery";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
} from "@/components/catalyst/navbar";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { UserFilter } from "@/lib/Components/DataTable/Filters/UserFilter";
import { Avatar } from "@/components/catalyst/avatar";
import { getInitials } from "@/lib/Utils/getInitials";

type OrganisationTableColumnId =
  | "name"
  | "is_verified"
  | "sales_agent"
  | "last_relocation_created_at"
  | "created_at";

const sortingColumn = {
  name: QueryOrganisationsOrderByColumn.Name,
  is_verified: QueryOrganisationsOrderByColumn.IsVerified,
  created_at: QueryOrganisationsOrderByColumn.CreatedAt,
  sales_agent: QueryOrganisationsOrderByColumn.SalesAgentName,
  last_relocation_created_at:
    QueryOrganisationsOrderByColumn.LastRelocationCreatedAt,
};

type VerificationFilter = "all" | "unverified" | "verified";

const isVerifiedByFilter: Record<VerificationFilter, boolean | undefined> = {
  all: undefined,
  unverified: false,
  verified: true,
};

export function OrganisationTable({ id }: { id: TableId }) {
  const getActions = useOrganisationActions();
  const [filter, setFilter] = useState<VerificationFilter>("all");
  const [salesAgentIds, setSalesAgentIds] = useState<string[]>([]);

  const columns: DataTableColDef<
    OrganisationListItem,
    any,
    OrganisationTableColumnId
  >[] = [
    {
      id: "name",
      accessorFn: (data) => data.name,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Avatar
            className="size-8"
            media={row.original.profilePicture}
            initials={getInitials(row.original.name)}
          />

          <ModelLinkCell
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to="/$tenant/$orgId/supplier/{-$supplierId}/organisations/$id"
            params={(prev) => ({ ...prev, id: row.original.id })}
          >
            {row.original.name}
          </ModelLinkCell>
        </div>
      ),
    },
    {
      id: "is_verified",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Verified" />
      ),
      accessorFn: (data) => (data.is_verified ? "Yes" : "No"),
      cell: ({ row }) => (
        <BadgeCell
          badges={[
            row.original.is_verified
              ? { label: "Verified", color: "green" }
              : { label: "Unverified", color: "amber" },
          ]}
        />
      ),
    },
    {
      id: "sales_agent",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Sales Agent" />
      ),
      accessorFn: (data) => data.salesAgent?.name ?? "--",
      cell: DeEmphasisedCell,
    },
    {
      id: "last_relocation_created_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Last Listing" />
      ),
      accessorFn: (data) => data.last_relocation_created_at,
      cell: ({ row }) =>
        row.original.last_relocation_created_at ? (
          <TimeSinceNow time={row.original.last_relocation_created_at} />
        ) : (
          <span className="text-sm text-gray-500">Never</span>
        ),
    },
    {
      id: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      accessorFn: (data) => data.created_at,
      cell: TimeSinceNowCell,
    },
  ];

  return (
    <div>
      <VerificationTabs filter={filter} onFilterChange={setFilter} />
      <DataTable
        columns={columns}
        getActions={getActions}
        document={organisationListQueryGraphql}
        hiddenColumns={filter === "all" ? [] : ["is_verified"]}
        filters={(table) => (
          <UserFilter
            placeholder="Sales Agent"
            selected={salesAgentIds}
            onChange={(ids) => {
              setSalesAgentIds(ids);
              table.setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
        )}
        initialSorting={[
          {
            id: "is_verified",
            desc: false,
          },
        ]}
        getQueryVariables={({ pagination, search, sorting }) => {
          const orderBy = sorting.map((sort) => ({
            order: sort.desc ? SortOrder.Desc : SortOrder.Asc,
            column: sortingColumn[sort.id],
          }));

          return {
            search,
            is_verified: isVerifiedByFilter[filter],
            sales_agent_ids: salesAgentIds.length ? salesAgentIds : undefined,
            page: pagination.pageIndex,
            first: pagination.pageSize,
            orderBy: !search ? orderBy : undefined,
          };
        }}
        accessor={(data) => data.organisations}
        id={id}
      />
    </div>
  );
}

function VerificationTabs({
  filter,
  onFilterChange,
}: {
  filter: VerificationFilter;
  onFilterChange: (filter: VerificationFilter) => void;
}) {
  const { data } = useGqlQuery(organisationVerificationCountsQuery, {});

  const unverifiedCount = data?.unverified.paginatorInfo.total ?? 0;
  const verifiedCount = data?.verified.paginatorInfo.total ?? 0;

  const tabs: {
    id: VerificationFilter;
    label: string;
    count?: number;
    badgeColor: BadgeColor;
  }[] = [
    {
      id: "all",
      label: "All",
      count: unverifiedCount + verifiedCount,
      badgeColor: "zinc",
    },
    {
      id: "unverified",
      label: "Unverified",
      count: unverifiedCount,
      badgeColor: "amber",
    },
    {
      id: "verified",
      label: "Verified",
      count: verifiedCount,
      badgeColor: "green",
    },
  ];

  return (
    <div className="relative border-gray-200 sm:border-b">
      <Navbar className="-mb-px">
        <NavbarSection id="organisation-navbar">
          {tabs.map((tab) => (
            <NavbarItem
              key={tab.id}
              current={filter === tab.id}
              onClick={() => onFilterChange(tab.id)}
            >
              {tab.label}
              {tab.count ? (
                <Badge className="py-0" color={tab.badgeColor}>
                  {tab.count}
                </Badge>
              ) : null}
            </NavbarItem>
          ))}
        </NavbarSection>
      </Navbar>
    </div>
  );
}
